import { Component, computed, DestroyRef, inject, Input, input, InputSignal, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { twMerge } from 'tailwind-merge';

@Component({
  selector: 'lat-lon-input',
  imports: [ReactiveFormsModule, TextboxComponent],
  templateUrl: './lat-lon-input.component.html',
  host: {
    '[class]': 'classes()'
  }
})
export class LatLonInputComponent implements OnInit, OnChanges {
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  public readonly type: InputSignal<'lat' | 'lon'> = input('lat');
  public readonly label: InputSignal<string> = input('');
  public readonly variant: InputSignal<'DDM' | 'DMS'> = input('DDM');
  public readonly hasPadding: InputSignal<boolean> = input(true);
  public readonly customClasses: InputSignal<string> = input('');
  public readonly hint: InputSignal<string> = input('');
  public readonly hintLabel: InputSignal<string> = input('');
  protected readonly requiredSignal: Signal<boolean> = computed(() => this.control.hasValidator(Validators.required));
  @Input() control: any = new FormControl();
  protected readonly classes: Signal<string> = computed(() =>
    twMerge('flex flex-col w-full  gap-2 overflow-hidden', this.hasPadding() ? 'px-9 py-3' : '', this.customClasses())
  );
  latFormGroup: any;
  lonFormGroup: any;
  LATITUDE_DIRECTIONS: string[] = ['SOUTH', 'NORTH'];
  LONGITUDE_DIRECTIONS: string[] = ['EAST', 'WEST'];

  constructor() {
    this.latFormGroup = this.formBuilder.group({
      degrees: [null, [Validators.min(0), Validators.max(89)]],
      minutes: [null, [Validators.min(0), Validators.max(59.9999)]],
      direction: [this.LATITUDE_DIRECTIONS[0]]
    });
    this.lonFormGroup = this.formBuilder.group({
      degrees: [null, [Validators.min(0), Validators.max(179)]],
      minutes: [null, [Validators.min(0), Validators.max(59.9999)]],
      direction: [this.LONGITUDE_DIRECTIONS[0]]
    });
  }

  ngOnInit() {
    if (this.control?.value) {
      this.updateFromDecimal(this.control?.value);
    }
    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.updateFromDecimal(value);
      }
    });
  }

  ngOnChanges(_changes: SimpleChanges) {}

  get form() {
    return this.type() === 'lat' ? this.latFormGroup : this.lonFormGroup;
  }

  updateCoordinates() {
    const formGroup = this.type() === 'lat' ? this.latFormGroup : this.lonFormGroup;
    const value = this.toDecimal(formGroup.get('degrees').value, formGroup.get('minutes').value, formGroup.get('direction').value);
    this.control.setValue(value.toFixed(6), { emitEvent: false });
  }

  private updateFromDecimal(value: number): void {
    const formGroup = this.type() === 'lat' ? this.latFormGroup : this.lonFormGroup;
    if (value !== undefined) {
      const latLon = this.fromDecimal(value, this.type());
      formGroup.patchValue(latLon);
    }
  }

  private toDecimal(deg: number, min: number, dir: string): number {
    const decimal = Number(deg) + Number(min) / 60;
    return dir === 'SOUTH' || dir === 'WEST' ? -decimal : decimal;
  }

  private fromDecimal(value: number, type: 'lat' | 'lon') {
    const dir = value >= 0 ? (type === 'lat' ? 'NORTH' : 'EAST') : type === 'lat' ? 'SOUTH' : 'WEST';
    const abs = Math.abs(value);
    const degrees = Math.floor(abs);
    const minutes = ((abs - degrees) * 60).toFixed(4);
    return { degrees, minutes, direction: dir };
  }

  onSuffixChange() {
    if (this.type() === 'lat') {
      this.latFormGroup
        .get('direction')
        .setValue(this.latFormGroup.get('direction').value === this.LATITUDE_DIRECTIONS[0] ? this.LATITUDE_DIRECTIONS[1] : this.LATITUDE_DIRECTIONS[0]);
    } else {
      this.lonFormGroup
        .get('direction')
        .setValue(this.lonFormGroup.get('direction').value === this.LONGITUDE_DIRECTIONS[0] ? this.LONGITUDE_DIRECTIONS[1] : this.LONGITUDE_DIRECTIONS[0]);
    }
    this.updateCoordinates();
  }

  preventInvalidDecimalInput(event: KeyboardEvent, max: number) {
    const input = event.target as HTMLInputElement;
    const key = event.key;

    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];

    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase())) {
      return;
    }

    if (allowedKeys.includes(key)) return;

    if (!/[0-9.]/.test(key)) {
      event.preventDefault();
      return;
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = input.value;
    const newValue = currentValue.slice(0, start) + key + currentValue.slice(end);

    if (key === '.' && currentValue.includes('.') && !currentValue.slice(start, end).includes('.')) {
      event.preventDefault();
      return;
    }

    if (!isNaN(Number(newValue)) && Number(newValue) > max) {
      event.preventDefault();
    }
  }
}
